import type { FormEvent, JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { PLAFOND_DEPOT_DEFAUT_OCTETS } from '@artosera/shared';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  Input,
  VisuallyHidden,
  useToast,
  type BadgeTone,
} from '@artosera/ui';
import { usePeut } from '../../../../auth/usePeut.js';
import {
  choisirSource,
  cleConnecteur,
  demanderAutorisation,
  envoyerCode,
  importEnCours,
  lancerImport,
  lireConnexion,
  listerDossiersDropbox,
  revoquerConnexion,
  type ConnexionDropbox,
  type EtatConnexion,
  type RevocationDistante,
} from '../../../../lib/connecteurs.js';
import { octetsEnGio } from '../../../../lib/depots.js';
import { formaterDate } from '../../../../lib/format.js';
import { estClos } from '../../../../lib/ouvertures.js';
import { NATURES_LOT, obtenirMandat, type NatureLot } from '../../../../lib/reprise.js';
import type { DossierOuverture } from '../../../../types.js';
import formStyles from '../../../../styles/form.module.css';
import pageStyles from '../../../../styles/page.module.css';
import { cleDepots } from '../../../tabs/reprise/DepotsBloc.js';
import { cleMandat } from '../../../tabs/reprise/MandatBloc.js';
import reprise from '../../../tabs/reprise/Reprise.module.css';
import { ChoixDossier } from './ChoixDossier.js';
import { SuiviImport } from './SuiviImport.js';
import { messageConnecteur } from './messages.js';
import styles from './Connecteur.module.css';

const TON_CONNEXION: Record<EtatConnexion, BadgeTone> = {
  aucune: 'neutral',
  en_attente: 'reserved',
  autorisee: 'available',
  revoquee: 'withdrawn',
  purgee: 'withdrawn',
};

/** Les natures cochées d'office quand le mandat les couvre : ce que Dropbox contient le plus souvent. */
const NATURES_COURANTES: readonly NatureLot[] = ['images', 'documents'];
const PLAFOND_DEFAUT_GIO = PLAFOND_DEPOT_DEFAUT_OCTETS / 1024 ** 3;

type EtatEtape = 'fait' | 'courant' | 'avenir';

function Etape({
  rang,
  etat,
  titre,
  children,
}: {
  rang: number;
  etat: EtatEtape;
  titre: string;
  children?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation('console');
  const cle = etat === 'avenir' ? 'aVenir' : etat;
  return (
    <li
      className={styles.etape}
      data-etat={etat}
      aria-current={etat === 'courant' ? 'step' : undefined}
    >
      <span className={styles.marqueur} aria-hidden="true">
        {etat === 'fait' ? <Check size={16} strokeWidth={2.5} /> : rang}
      </span>
      <div className={styles.corps}>
        <h3 className={styles.etapeTitre}>
          {titre}
          <VisuallyHidden> ({t(`importDropbox.etatEtape.${cle}`)})</VisuallyHidden>
        </h3>
        {etat === 'avenir' ? null : children}
      </div>
    </li>
  );
}

const heure = (valeur: string): string =>
  new Date(valeur).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

/**
 * Fiche dossier › Reprise › « Importer depuis une application » (ARTO-135) :
 * autoriser Dropbox, coller le code, choisir le dossier, lancer l'import,
 * le suivre, révoquer. Tout ce que l'écran montre vient de la connexion que
 * l'API rend ; le jeton n'y est jamais.
 */
export function ConnecteurDropbox({ dossier }: { dossier: DossierOuverture }): JSX.Element | null {
  const peut = usePeut();
  if (!peut('console:run-import')) return null;
  return <Connecteur dossier={dossier} />;
}

function Connecteur({ dossier }: { dossier: DossierOuverture }): JSX.Element {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const { toast } = useToast();
  const idGalerie = dossier.galleryId;
  const clos = estClos(dossier.etat);
  const cle = cleConnecteur(dossier.id);

  const [erreur, setErreur] = useState<string | null>(null);
  const [adresse, setAdresse] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [modifierDossier, setModifierDossier] = useState(false);
  const [choix, setChoix] = useState<string | null>(null);
  const [natures, setNatures] = useState<NatureLot[] | null>(null);
  const [plafond, setPlafond] = useState(String(PLAFOND_DEFAUT_GIO));
  const [revocation, setRevocation] = useState(false);
  const [resultat, setResultat] = useState<RevocationDistante | null>(null);
  const [maintenant, setMaintenant] = useState(() => Date.now());

  const lecture = useQuery({
    queryKey: cle,
    queryFn: async () => (await lireConnexion(dossier.id)).connexion,
    enabled: idGalerie !== null,
    // Un import qui tourne se suit, comme les autres travaux de la reprise.
    refetchInterval: (requete) => (importEnCours(requete.state.data?.import) ? 3000 : false),
  });
  const connexion: ConnexionDropbox | undefined = lecture.data;
  const autorisee = connexion?.etat === 'autorisee';
  const source = connexion?.source ?? null;
  const lancement = connexion?.import ?? null;
  const vivant = importEnCours(lancement);
  const choixOuvert = autorisee && (source === null || modifierDossier);

  // L'attente du code se périme : l'écran le voit sans rechargement.
  const enAttente = connexion?.etat === 'en_attente';
  useEffect(() => {
    if (!enAttente) return undefined;
    const minuteur = window.setInterval(() => setMaintenant(Date.now()), 15_000);
    return () => window.clearInterval(minuteur);
  }, [enAttente]);

  // Un import fini remplit un dépôt : la liste des dépôts de la galerie se relit.
  const etatImport = lancement?.etat ?? null;
  useEffect(() => {
    if (idGalerie !== null && (etatImport === 'termine' || etatImport === 'plafond_atteint'))
      void client.invalidateQueries({ queryKey: cleDepots(idGalerie) });
  }, [client, idGalerie, etatImport]);

  const dossiers = useQuery({
    queryKey: [...cle, 'dossiers'],
    queryFn: () => listerDossiersDropbox(dossier.id),
    enabled: choixOuvert,
    retry: false,
  });
  const mandat = useQuery({
    queryKey: cleMandat(idGalerie ?? ''),
    queryFn: () => obtenirMandat(idGalerie ?? ''),
    enabled: autorisee && idGalerie !== null,
  });

  const appliquer = (reponse: { connexion: ConnexionDropbox }): void => {
    setErreur(null);
    client.setQueryData(cle, reponse.connexion);
  };
  const echouer = (cause: unknown): void => setErreur(messageConnecteur(t, cause));

  const autoriser = useMutation({
    mutationFn: () => demanderAutorisation(dossier.id),
    onSuccess: (reponse) => {
      appliquer(reponse);
      setAdresse(reponse.url);
      setResultat(null);
      setCode('');
      // Dans un nouvel onglet : la console reste là, le code se colle ensuite.
      window.open(reponse.url, '_blank', 'noopener,noreferrer');
    },
    onError: echouer,
  });
  const coller = useMutation({
    mutationFn: () => envoyerCode(dossier.id, code.trim()),
    onSuccess: (reponse) => {
      appliquer(reponse);
      setAdresse(null);
      setCode('');
      setModifierDossier(false);
    },
    onError: echouer,
  });
  const choisir = useMutation({
    mutationFn: (chemin: string) => choisirSource(dossier.id, chemin),
    onSuccess: (reponse) => {
      appliquer(reponse);
      setModifierDossier(false);
    },
    onError: echouer,
  });
  const lancer = useMutation({
    mutationFn: (corps: { natures: NatureLot[]; plafondOctets?: number }) =>
      lancerImport(dossier.id, corps),
    onSuccess: (reponse) => {
      appliquer(reponse);
      toast({
        title: t(
          reponse.reprise ? 'importDropbox.lancer.reprisDansDepot' : 'importDropbox.lancer.lance',
        ),
        closeLabel: t('fiche.domaines.fermer'),
      });
      if (idGalerie !== null) void client.invalidateQueries({ queryKey: cleDepots(idGalerie) });
    },
    onError: echouer,
  });
  const revoquer = useMutation({
    mutationFn: () => revoquerConnexion(dossier.id),
    onSuccess: (reponse) => {
      appliquer(reponse);
      setAdresse(null);
      setRevocation(false);
      setModifierDossier(false);
      setResultat(reponse.revocationDistante);
    },
  });
  // Annuler une autorisation demandée mais jamais conclue : rien à révoquer chez Dropbox.
  const annuler = useMutation({
    mutationFn: () => revoquerConnexion(dossier.id),
    onSuccess: (reponse) => {
      appliquer(reponse);
      setAdresse(null);
    },
    onError: echouer,
  });

  const ouvrirChoix = (): void => {
    setChoix(source?.chemin ?? null);
    setModifierDossier(true);
  };

  // ── Cas sans étapes ──────────────────────────────────────────────────────

  const entete = (badge: ReactNode): JSX.Element => (
    <div className={pageStyles.sectionTitre}>
      <h2 id="ouv-connecteur">{t('importDropbox.titre')}</h2>
      {badge}
    </div>
  );

  if (idGalerie === null) {
    return (
      <section className={pageStyles.panneau} aria-labelledby="ouv-connecteur">
        {entete(null)}
        <p className={styles.sobre}>{t('importDropbox.sansGalerie')}</p>
      </section>
    );
  }
  if (lecture.isLoading) {
    return (
      <section className={pageStyles.panneau} aria-labelledby="ouv-connecteur">
        {entete(null)}
        <p className={styles.sobre}>{t('importDropbox.chargement')}</p>
      </section>
    );
  }
  if (lecture.isError || !connexion) {
    return (
      <section className={pageStyles.panneau} aria-labelledby="ouv-connecteur">
        {entete(null)}
        <p className={reprise.erreur} role="alert">
          {messageConnecteur(t, lecture.error) ?? t('importDropbox.erreurLecture')}
        </p>
      </section>
    );
  }
  if (!connexion.disponible) {
    return (
      <section className={pageStyles.panneau} aria-labelledby="ouv-connecteur">
        {entete(null)}
        <p className={styles.sobre}>{t('importDropbox.indisponible')}</p>
        <p className={reprise.discret}>{t('importDropbox.indisponibleAide')}</p>
      </section>
    );
  }

  // ── Les étapes ───────────────────────────────────────────────────────────

  const expireLe = connexion.autorisationExpireLe;
  const expiree = expireLe !== null && new Date(expireLe).getTime() <= maintenant;
  const proposees: readonly NatureLot[] = mandat.isError
    ? NATURES_LOT
    : (mandat.data?.actif?.natures ?? []);
  const choisies = natures ?? NATURES_COURANTES.filter((nature) => proposees.includes(nature));
  const plafondOctets = octetsEnGio(Number(plafond) || PLAFOND_DEFAUT_GIO);

  const etapeAutoriser: EtatEtape =
    connexion.etat === 'en_attente' || autorisee ? 'fait' : 'courant';
  const etapeCode: EtatEtape =
    connexion.etat === 'en_attente' ? 'courant' : autorisee ? 'fait' : 'avenir';
  const etapeDossier: EtatEtape = !autorisee ? 'avenir' : choixOuvert ? 'courant' : 'fait';
  const etapeLancer: EtatEtape =
    !autorisee || choixOuvert ? 'avenir' : lancement ? 'fait' : 'courant';
  const etapeSuivi: EtatEtape = lancement === null ? 'avenir' : vivant ? 'courant' : 'fait';
  const actif = !clos;

  const soumettreCode = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (code.trim() !== '') coller.mutate();
  };
  const soumettreLancement = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (choisies.length > 0) lancer.mutate({ natures: choisies, plafondOctets });
  };

  return (
    <section className={pageStyles.panneau} aria-labelledby="ouv-connecteur">
      {entete(
        <Badge tone={TON_CONNEXION[connexion.etat]}>
          {t(`importDropbox.etats.${connexion.etat}`)}
        </Badge>,
      )}
      <p className={reprise.texte}>{t('importDropbox.texte')}</p>

      <div className={reprise.pile}>
        {connexion.mode === 'simule' ? (
          <p className={reprise.avertissement} role="note">
            {t('importDropbox.simule')}
          </p>
        ) : null}
        {clos ? (
          <p className={reprise.avertissement} role="note">
            {t('importDropbox.clos')}
          </p>
        ) : null}
        {resultat !== null ? (
          <div
            className={styles.resultat}
            data-ton={resultat === 'echec' ? 'echec' : 'ok'}
            role="status"
          >
            <strong>{t('importDropbox.revoquer.resultat.titre')}</strong>
            <span>{t(`importDropbox.revoquer.resultat.${resultat}`)}</span>
          </div>
        ) : null}
        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {erreur}
          </p>
        ) : null}

        <ol className={styles.etapes} aria-label={t('importDropbox.etapes')}>
          <Etape rang={1} etat={etapeAutoriser} titre={t('importDropbox.autoriser.titre')}>
            {autorisee ? (
              <p className={styles.ligneFaite}>
                {connexion.compte
                  ? t('importDropbox.autoriser.fait', {
                      compte: connexion.compte,
                      par: connexion.autoriseePar ?? '—',
                      date: formaterDate(connexion.autoriseeLe, '—'),
                    })
                  : t('importDropbox.autoriser.faitSansCompte', {
                      par: connexion.autoriseePar ?? '—',
                      date: formaterDate(connexion.autoriseeLe, '—'),
                    })}
              </p>
            ) : connexion.etat === 'en_attente' ? (
              <p className={styles.ligneFaite}>
                {expiree || expireLe === null
                  ? t('importDropbox.autoriser.expiree')
                  : t('importDropbox.autoriser.expire', { heure: heure(expireLe) })}
              </p>
            ) : (
              <>
                <p className={styles.ligneAide}>{t('importDropbox.autoriser.texte')}</p>
                {connexion.etat === 'revoquee' ? (
                  <p className={styles.ligneAide}>
                    {t('importDropbox.autoriser.revoquee', {
                      date: formaterDate(connexion.revoqueLe, '—'),
                    })}
                  </p>
                ) : null}
                {connexion.etat === 'purgee' ? (
                  <p className={styles.ligneAide}>
                    {t('importDropbox.autoriser.purgee', {
                      date: formaterDate(connexion.purgeLe, '—'),
                    })}
                  </p>
                ) : null}
                <div className={reprise.actions}>
                  <Button
                    loading={autoriser.isPending}
                    disabled={!actif}
                    onClick={() => autoriser.mutate()}
                  >
                    {t('importDropbox.autoriser.bouton')}
                  </Button>
                </div>
              </>
            )}
          </Etape>

          <Etape rang={2} etat={etapeCode} titre={t('importDropbox.code.titre')}>
            {connexion.etat === 'en_attente' ? (
              <form className={reprise.pile} onSubmit={soumettreCode}>
                <p className={styles.ligneAide}>{t('importDropbox.code.texte')}</p>
                {!expiree && adresse === null ? (
                  <p className={styles.ligneAide}>{t('importDropbox.autoriser.sansAdresse')}</p>
                ) : null}
                <div className={`${formStyles.champ} ${styles.codeChamp}`}>
                  <label htmlFor="connecteur-code">{t('importDropbox.code.champ')}</label>
                  <Input
                    id="connecteur-code"
                    value={code}
                    maxLength={512}
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    disabled={expiree}
                    onChange={(evenement) => setCode(evenement.target.value)}
                  />
                </div>
                <div className={reprise.actions}>
                  <Button
                    type="submit"
                    loading={coller.isPending}
                    disabled={!actif || expiree || code.trim() === ''}
                  >
                    {t('importDropbox.code.valider')}
                  </Button>
                  {adresse !== null && !expiree ? (
                    <a
                      className={styles.lienBouton}
                      href={adresse}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('importDropbox.autoriser.rouvrir')}
                    </a>
                  ) : null}
                  <Button
                    variant={expiree || adresse === null ? 'secondary' : 'ghost'}
                    loading={autoriser.isPending}
                    disabled={!actif}
                    onClick={() => autoriser.mutate()}
                  >
                    {t('importDropbox.autoriser.nouvelle')}
                  </Button>
                  <Button
                    variant="ghost"
                    loading={annuler.isPending}
                    onClick={() => annuler.mutate()}
                  >
                    {t('importDropbox.revoquer.annulerAutorisation')}
                  </Button>
                </div>
              </form>
            ) : autorisee ? (
              <p className={styles.ligneFaite}>{t('importDropbox.code.fait')}</p>
            ) : null}
          </Etape>

          <Etape rang={3} etat={etapeDossier} titre={t('importDropbox.dossier.titre')}>
            {choixOuvert ? (
              <>
                <p className={styles.ligneAide}>{t('importDropbox.dossier.texte')}</p>
                {dossiers.isLoading ? (
                  <p className={styles.sobre}>{t('importDropbox.dossier.chargement')}</p>
                ) : null}
                {dossiers.isError ? (
                  <p className={reprise.erreur} role="alert">
                    {messageConnecteur(t, dossiers.error) ?? t('importDropbox.dossier.erreur')}
                  </p>
                ) : null}
                {dossiers.isSuccess ? (
                  <>
                    <ChoixDossier
                      dossiers={dossiers.data.dossiers}
                      valeur={choix}
                      onChoisir={setChoix}
                    />
                    {dossiers.data.dossiers.length === 0 ? (
                      <p className={reprise.discret}>{t('importDropbox.dossier.aucun')}</p>
                    ) : null}
                    {dossiers.data.tronque ? (
                      <p className={reprise.discret}>{t('importDropbox.dossier.tronque')}</p>
                    ) : null}
                  </>
                ) : null}
                <div className={reprise.actions}>
                  <Button
                    loading={choisir.isPending}
                    disabled={!actif || choix === null || !dossiers.isSuccess}
                    onClick={() => {
                      if (choix !== null) choisir.mutate(choix);
                    }}
                  >
                    {t('importDropbox.dossier.utiliser')}
                  </Button>
                  {source !== null ? (
                    <Button variant="ghost" onClick={() => setModifierDossier(false)}>
                      {t('importDropbox.dossier.garder')}
                    </Button>
                  ) : null}
                </div>
              </>
            ) : autorisee && source !== null ? (
              <>
                <p className={styles.ligneFaite}>
                  {source.chemin === ''
                    ? t('importDropbox.dossier.choisiTout')
                    : t('importDropbox.dossier.choisi', { chemin: source.chemin })}
                </p>
                <div className={reprise.actions}>
                  <Button variant="ghost" disabled={!actif || vivant} onClick={ouvrirChoix}>
                    {t('importDropbox.dossier.changer')}
                  </Button>
                </div>
                {vivant ? (
                  <p className={reprise.discret}>{t('importDropbox.dossier.changerImpossible')}</p>
                ) : null}
              </>
            ) : null}
          </Etape>

          <Etape rang={4} etat={etapeLancer} titre={t('importDropbox.lancer.titre')}>
            {etapeLancer === 'courant' ? (
              <form className={reprise.pile} onSubmit={soumettreLancement}>
                <p className={styles.ligneAide}>{t('importDropbox.lancer.texte')}</p>
                {mandat.isLoading ? (
                  <p className={styles.sobre}>{t('reprise.chargement')}</p>
                ) : proposees.length === 0 ? (
                  <p className={reprise.avertissement}>
                    {t('importDropbox.lancer.aucuneNature')}{' '}
                    <Link to={`/galeries/${idGalerie}/reprise`} className="lien-cible">
                      {t('importDropbox.lancer.lienMandat')}
                    </Link>
                  </p>
                ) : (
                  <fieldset className={reprise.natures}>
                    <legend>{t('importDropbox.lancer.natures')}</legend>
                    <div className={styles.naturesListe}>
                      {proposees.map((nature) => (
                        <Checkbox
                          key={nature}
                          id={`connecteur-nature-${nature}`}
                          label={t(`reprise.natures.${nature}`)}
                          checked={choisies.includes(nature)}
                          onCheckedChange={(coche) =>
                            setNatures(
                              coche === true
                                ? [...choisies.filter((n) => n !== nature), nature]
                                : choisies.filter((n) => n !== nature),
                            )
                          }
                        />
                      ))}
                    </div>
                  </fieldset>
                )}
                <div className={`${formStyles.champ} ${styles.plafond}`}>
                  <label htmlFor="connecteur-plafond">{t('importDropbox.lancer.plafond')}</label>
                  <Input
                    id="connecteur-plafond"
                    type="number"
                    inputMode="decimal"
                    min={0.001}
                    max={200}
                    step="any"
                    value={plafond}
                    onChange={(evenement) => setPlafond(evenement.target.value)}
                  />
                  <span className={formStyles.aide}>{t('importDropbox.lancer.plafondAide')}</span>
                </div>
                {choisies.length === 0 && proposees.length > 0 ? (
                  <p className={reprise.discret}>{t('importDropbox.lancer.choisirNature')}</p>
                ) : null}
                <div className={reprise.actions}>
                  <Button
                    type="submit"
                    loading={lancer.isPending}
                    disabled={!actif || choisies.length === 0}
                  >
                    {t('importDropbox.lancer.lancer')}
                  </Button>
                </div>
              </form>
            ) : lancement ? (
              <p className={styles.ligneFaite}>
                {lancement.natures.map((nature) => t(`reprise.natures.${nature}`)).join(', ')}
              </p>
            ) : null}
          </Etape>

          <Etape rang={5} etat={etapeSuivi} titre={t('importDropbox.suivi.titre')}>
            {lancement ? (
              <SuiviImport
                idGalerie={idGalerie}
                lancement={lancement}
                peutRelancer={autorisee && actif}
                relancement={lancer.isPending}
                onRelancer={() => lancer.mutate({ natures: lancement.natures })}
              />
            ) : null}
          </Etape>
        </ol>

        {autorisee ? (
          <div className={styles.pied}>
            <Button variant="ghost" onClick={() => setRevocation(true)}>
              {t('importDropbox.revoquer.bouton')}
            </Button>
          </div>
        ) : null}
      </div>

      <Dialog
        open={revocation}
        onOpenChange={setRevocation}
        title={t('importDropbox.revoquer.titre')}
        description={t('importDropbox.revoquer.texte')}
        closeLabel={t('importDropbox.revoquer.annuler')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRevocation(false)}>
              {t('importDropbox.revoquer.annuler')}
            </Button>
            <Button variant="danger" loading={revoquer.isPending} onClick={() => revoquer.mutate()}>
              {t('importDropbox.revoquer.confirmer')}
            </Button>
          </>
        }
      >
        {revoquer.isError ? (
          <p className={reprise.erreur} role="alert">
            {messageConnecteur(t, revoquer.error)}
          </p>
        ) : null}
      </Dialog>
    </section>
  );
}
